import {
  Checkbox,
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { SelectHTMLAttributes, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';
import clsx from 'clsx';
import { ListPill } from '../CustomerTable/CustomerTable';

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
  options: { value: string; label: string; icon?: string | React.ReactNode }[];
  showDefaultOption?: boolean;
  sizeStyle?: 'sm' | 'md' | 'lg';
  label?: string;
  isOptional?: boolean;
  showPill?: boolean;
  virtual?: {
    options?: { value: string; label: string; icon?: string }[];
  };
  maxDisplayedItems?: number;
}

const getStyle = (size: string) => {
  switch (size) {
    case 'sm': {
      return 'text-sm py-2 px-3';
    }
    case 'md': {
      return 'text-normal py-3 px-4';
    }
    case 'lg': {
      return 'text-lg py-4 px-5';
    }
  }
};

export const CheckSelectField = ({
  name,
  placeholder,
  options,
  showDefaultOption = false,
  sizeStyle = 'md',
  label,
  isOptional,
  showPill,
  virtual,
  maxDisplayedItems = 3,
  ...rest
}: SelectFieldProps) => {
  const {
    control,
    getValues,
    // @ts-expect-error this are need to update the input state on errors/etc
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    formState: { errors, isDirty },
  } = useFormContext();

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const fieldValues = getValues(name ?? '');

  const getOptionLabelByValue = (
    optionValues: string[] | string | undefined
  ): string => {
    if (!optionValues || !options) return '';

    // Ensure optionValues is always an array
    const valuesArray = Array.isArray(optionValues)
      ? optionValues
      : [optionValues];

    if (valuesArray.length === 0) return '';

    const labels = valuesArray
      .map((value) => options.find((opt) => opt.value === value)?.label)
      .filter((label): label is string => label !== undefined);

    if (labels.length <= maxDisplayedItems) {
      return labels.join(', ');
    } else {
      return `${labels.slice(0, maxDisplayedItems).join(', ')} ... (${labels.length - maxDisplayedItems} more)`;
    }
  };

  useEffect(() => {
    if (fieldValues) {
      const valuesArray = Array.isArray(fieldValues)
        ? fieldValues
        : [fieldValues];
      if (valuesArray.length > 0) {
        setSelected(valuesArray);
      }
    }
  }, [fieldValues]);

  return (
    <Controller
      control={control}
      name={name ?? ''}
      defaultValue={rest.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <div className="w-full">
            <Combobox
              multiple
              {...(virtual && { virtual: { options: virtual.options } })}
              immediate
              value={value}
              onChange={(event) => {
                onChange(event);
                setSelected(event);
              }}
              onClose={() => setQuery('')}
            >
              {() => (
                <>
                  {label && (
                    <label
                      htmlFor={name}
                      className="font-medium text-sm inline-block mb-1"
                    >
                      {label}{' '}
                      {isOptional && (
                        <span className="text-base-coolGray">(Optional)</span>
                      )}
                    </label>
                  )}
                  <div className="relative w-full">
                    <div className="relative w-full">
                      <ComboboxInput
                        aria-label={name}
                        onChange={(event) => setQuery(event.target.value ?? '')}
                        placeholder={placeholder}
                        value={value ? getOptionLabelByValue(value) : ''}
                        className={clsx(
                          `group bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${rest.className ?? ''} ${getStyle(sizeStyle)} truncate pr-10`,
                          { 'border border-system-error': name && errors[name] }
                        )}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <ComboboxOptions
                    anchor="bottom"
                    className="min-w-[--input-width] rounded-lg shadow-lg bg-white mt-2 !max-h-[400px] "
                  >
                    {showDefaultOption && (
                      <option
                        value="null"
                        disabled
                        className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b"
                      >
                        Choose an option
                      </option>
                    )}
                    {query.length > 0 &&
                      options
                        ?.filter((option) =>
                          option?.label
                            ?.toLowerCase()
                            .includes(query.toLowerCase())
                        )
                        .map((option) => (
                          <ComboboxOption
                            key={option.value}
                            value={option.value}
                            className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt"
                          >
                            {({ selected }) => (
                              <div className="flex justify-between items-center gap-2">
                                <div className="flex items-center gap-3">
                                  {option.icon &&
                                    (typeof option.icon === 'string' ? (
                                      <img
                                        src={option.icon}
                                        alt=""
                                        className="w-5 h-5 text-gray-400"
                                      />
                                    ) : (
                                      <div className="w-5 h-5 text-gray-400">
                                        {option.icon as React.ReactNode}
                                      </div>
                                    ))}
                                  <span className={selected ? 'font-bold' : ''}>
                                    {option.label}
                                  </span>
                                </div>
                                <div>
                                  <Checkbox
                                    checked={selected}
                                    className="group block size-4 rounded border bg-white data-[checked]:bg-brand-baseBlue data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[checked]:data-[disabled]:bg-gray-500"
                                  >
                                    <svg
                                      className="stroke-white opacity-0 group-data-[checked]:opacity-100"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                    >
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </Checkbox>
                                </div>
                              </div>
                            )}
                          </ComboboxOption>
                        ))}
                    {(!query || query.length === 0) &&
                      options?.map((option) => (
                        <ComboboxOption
                          key={option.value}
                          value={option.value}
                          className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt"
                        >
                          {({ selected }) => (
                            <div className="flex justify-between items-center gap-2">
                              <div className="flex items-center gap-3">
                                {option.icon &&
                                  (typeof option.icon === 'string' ? (
                                    <img
                                      src={option.icon}
                                      alt=""
                                      className="w-5 h-5 text-gray-400"
                                    />
                                  ) : (
                                    <div className="w-5 h-5 text-gray-400">
                                      {option.icon as React.ReactNode}
                                    </div>
                                  ))}
                                <span className={selected ? 'font-bold' : ''}>
                                  {option.label}
                                </span>
                              </div>
                              <div>
                                <Checkbox
                                  checked={selected}
                                  className="group block size-4 rounded data-[checked]:border-brand-baseBlue border border-base-frenchGray bg-white data-[checked]:bg-brand-baseBlue data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[checked]:data-[disabled]:bg-gray-500"
                                >
                                  <svg
                                    className="stroke-white opacity-0 group-data-[checked]:opacity-100"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                  >
                                    <path
                                      d="M3 8L6 11L11 3.5"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </Checkbox>
                              </div>
                            </div>
                          )}
                        </ComboboxOption>
                      ))}
                  </ComboboxOptions>
                </>
              )}
            </Combobox>
            {showPill && selected && selected.length > 0 && options && (
              <div className="flex gap-2 mt-2 flex-wrap max-h-[200px] h-fit overflow-y-auto">
                {selected.map((selectedValue, index) => {
                  const label = options.find(
                    (opt) => opt.value === selectedValue
                  )?.label;
                  return <ListPill key={index} name={label ?? ''} />;
                })}
              </div>
            )}
            {name && errors[name] && (
              <p role="alert" className="text-system-error">
                <p
                  role="alert"
                  className="text-system-error text-sm text-left pt-1"
                >
                  {typeof errors?.[name]?.message === 'string'
                    ? errors[name]?.message
                    : ''}
                </p>
              </p>
            )}
          </div>
        );
      }}
    />
  );
};
